/**
 * @fileoverview UC-REG-303 — Lógica de presentación del CRM de órdenes
 *   (`OrdersCRM.vue`, que no se monta bajo vue-jest): tipos según el tenant
 *   (medicamentos con `VUE_APP_ORDERS_CRM_MEDICATIONS`), estados con
 *   "Orden consumida" (`fulfilled`, terminal), resumen de la receta y
 *   visibilidad del botón de WhatsApp (`VUE_APP_ORDERS_CRM_WHATSAPP`).
 */
import {
  isOrdersCrmMedicationsEnabled,
  isOrdersCrmWhatsappEnabled,
} from "@/services/config";

/** @description Tipos históricos del CRM (UC-REG-162). */
const BASE_TYPE_META = {
  lab: { icon: "labs", label: "Lab", filterLabel: "🔬 Lab" },
  images: { icon: "radiology", label: "Imagen", filterLabel: "🩻 Imagen" },
  practices: {
    icon: "assignment",
    label: "Práctica",
    filterLabel: "📋 Práctica",
  },
};

/** @description UC-REG-303 — Tipo de receta de medicamentos. */
const MEDICATIONS_TYPE_META = {
  icon: "pill",
  label: "Medicamento",
  filterLabel: "💊 Medicamento",
};

/**
 * @description Estados del CRM. `fulfilled` ("Orden consumida") es
 *   terminal con éxito y vale para cualquier tipo.
 */
export const STATUS_META = {
  new: { label: "Nueva", cls: "is-new" },
  contacted: { label: "Contactado", cls: "is-contacted" },
  "appointment-proposed": { label: "Turno propuesto", cls: "is-proposed" },
  "appointment-taken": { label: "Turno tomado", cls: "is-taken" },
  fulfilled: { label: "Orden consumida", cls: "is-fulfilled" },
  closed: { label: "Cerrado", cls: "is-closed" },
};

/** @description Estados terminales (sin acciones). */
export const TERMINAL_STATUSES = ["appointment-taken", "fulfilled", "closed"];

/** @description CSV de estados activos para el filtro "Activas". */
export const ACTIVE_STATUS_CSV = "new,contacted,appointment-proposed";

/**
 * @description Metadatos de tipo del CRM según el tenant.
 * @param {boolean} [medications] - Default: flag del build
 * @returns {Object<string,{icon:string,label:string,filterLabel:string}>}
 */
export function crmTypeMeta(medications = isOrdersCrmMedicationsEnabled) {
  return medications
    ? { ...BASE_TYPE_META, medications: MEDICATIONS_TYPE_META }
    : { ...BASE_TYPE_META };
}

/**
 * @description Meta de un tipo (fallback genérico para tipos desconocidos).
 * @param {string} type
 * @param {boolean} [medications]
 * @returns {{icon:string,label:string}}
 */
export function typeMetaOf(type, medications = isOrdersCrmMedicationsEnabled) {
  return crmTypeMeta(medications)[type] || { icon: "assignment", label: type };
}

/**
 * @description Opciones del select de tipo.
 * @param {boolean} [medications]
 * @returns {Array<{value:string,label:string}>}
 */
export function typeFilterOptions(medications = isOrdersCrmMedicationsEnabled) {
  const meta = crmTypeMeta(medications);
  return Object.keys(meta).map((value) => ({
    value,
    label: meta[value].filterLabel,
  }));
}

/**
 * @description Meta de un estado (fallback 'is-new').
 * @param {string} status
 * @returns {{label:string,cls:string}}
 */
export function statusMetaOf(status) {
  return STATUS_META[status] || { label: status, cls: "is-new" };
}

/**
 * @description El estado es terminal (no admite más acciones).
 * @param {string} status
 * @returns {boolean}
 */
export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.includes(status || "new");
}

/**
 * @description Texto corto de una receta para la tabla/detalle:
 *   "Ibuprofeno 600 mg x2".
 * @param {Object} row - Indication de tipo medications
 * @returns {string}
 */
export function medicationSummary(row) {
  if (!row) return "";
  const name =
    (row.publicMedication && row.publicMedication.name) ||
    (row.medication && row.medication.name) ||
    row.title ||
    "Medicamento";
  return row.quantity ? `${name} x${row.quantity}` : name;
}

/**
 * @description Texto del encabezado de la bandeja según los tipos.
 * @param {boolean} [medications]
 * @returns {string}
 */
export function crmHint(medications = isOrdersCrmMedicationsEnabled) {
  return medications
    ? "Órdenes de lab, imagen, prácticas y recetas de medicamentos emitidas en el centro. Contactá al paciente y marcá la orden como consumida cuando se concrete."
    : "Órdenes de lab, imagen y prácticas emitidas en el centro. Contactá al paciente para ofrecerle turno.";
}

/**
 * @description El botón de WhatsApp del modal se muestra (flag del build,
 *   default ON).
 * @param {boolean} [enabled]
 * @returns {boolean}
 */
export function showWhatsappAction(enabled = isOrdersCrmWhatsappEnabled) {
  return Boolean(enabled);
}
